
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';

export default function EarningsGuidePage() {
  const [selectedTab, setSelectedTab] = useState('overview');

  const earningTabs = [
    { id: 'overview', name: 'Overview', icon: 'ri-pie-chart-line' },
    // Fixed string literal: used a single back‑slash to escape the apostrophe
    { id: 'calculation', name: "How It's Calculated", icon: 'ri-calculator-line' },
    { id: 'maximize', name: 'Maximize Earnings', icon: 'ri-rocket-line' },
    { id: 'payouts', name: 'Payouts', icon: 'ri-bank-card-line' },
    { id: 'bonuses', name: 'Bonuses & Incentives', icon: 'ri-gift-line' }
  ];

  const earningExamples = [
    {
      service: 'House Cleaning (2 hours)',
      basePrice: '₹400',
      commission: '₹320 (80%)',
      bonus: '₹40',
      total: '₹360',
      time: '2 hrs'
    },
    {
      service: 'AC Repair Service',
      basePrice: '₹500',
      commission: '₹400 (80%)',
      bonus: '₹50',
      total: '₹450',
      time: '1.5 hrs'
    },
    {
      service: 'Beauty & Grooming',
      basePrice: '₹800',
      commission: '₹640 (80%)',
      bonus: '₹80',
      total: '₹720',
      time: '2.5 hrs'
    },
    {
      service: 'Plumbing Repair',
      basePrice: '₹300',
      commission: '₹240 (80%)',
      bonus: '₹30',
      total: '₹270',
      time: '1 hr'
    }
  ];

  const bonusStructure = [
    {
      type: 'Performance Bonus',
      criteria: 'Rating ≥ 4.5 stars',
      reward: '10% extra on all jobs',
      icon: 'ri-star-line'
    },
    {
      type: 'Peak Hour Bonus',
      criteria: 'Jobs completed 8-11 AM, 6-9 PM',
      reward: '₹50 extra per job',
      icon: 'ri-time-line'
    },
    {
      type: 'Weekly Target Bonus',
      criteria: 'Complete 25+ jobs per week',
      reward: '₹1000 bonus',
      icon: 'ri-trophy-line'
    },
    {
      type: 'Referral Bonus',
      criteria: 'Each successful partner referral',
      reward: '₹2500 one-time',
      icon: 'ri-user-add-line'
    },
    {
      type: 'Customer Retention',
      criteria: 'Repeat customer bookings',
      reward: '₹25 per repeat booking',
      icon: 'ri-heart-line'
    },
    {
      type: 'Perfect Attendance',
      criteria: 'No missed jobs in a month',
      reward: '₹3000 monthly bonus',
      icon: 'ri-calendar-check-line'
    }
  ];

  const tips = [
    {
      title: 'Maintain High Ratings',
      desc: 'Excellent service leads to better ratings, more jobs, and performance bonuses.',
      icon: 'ri-star-line'
    },
    {
      title: 'Work During Peak Hours',
      desc: 'Earn extra money by taking jobs during high-demand time slots.',
      icon: 'ri-time-line'
    },
    {
      title: 'Complete More Services',
      desc: "The more services you can offer, the more job opportunities you'll receive.",
      icon: 'ri-tools-line'
    },
    {
      title: 'Be Punctual & Professional',
      desc: 'Reliability leads to repeat customers and better ratings.',
      icon: 'ri-user-star-line'
    },
    {
      title: 'Upsell Additional Services',
      desc: 'Suggest complementary services to increase your earnings per visit.',
      icon: 'ri-add-circle-line'
    },
    {
      title: 'Work Consistently',
      desc: 'Regular availability helps you hit weekly targets and qualify for bonuses.',
      icon: 'ri-calendar-line'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
      <Navbar />

      {/* Hero Section */}
      <section
        className="py-16 bg-gradient-to-r from-purple-600 to-purple-700 text-white relative overflow-hidden"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=service%20partner%20counting%20money%20and%20looking%20at%20earnings%20chart%20on%20smartphone%2C%20purple%20and%20white%20theme%2C%20financial%20success%20concept%2C%20professional%20worker%20with%20tools%20and%20payment%20information%2C%20clean%20modern%20design&width=1200&height=500&seq=earnings-guide-hero&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-purple-600/80"></div>
        <div className="container mx-auto px-6 relative z-10 text-center">
          <h1 className="text-4xl font-bold mb-4">Partner Earnings Guide</h1>
          <p className="text-xl max-w-2xl mx-auto">
            Understand how you earn, when you get paid, and how to maximize your income with BNM
          </p>
        </div>
      </section>

      {/* Tabs Navigation */}
      <section className="py-8 bg-white shadow-sm">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap gap-2 justify-center">
              {earningTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  className={`flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    selectedTab === tab.id
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <i className={`${tab.icon} mr-2`}></i>
                  {tab.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            {/* Overview Tab */}
            {selectedTab === 'overview' && (
              <div className="space-y-12">
                <div className="text-center">
                  <h2 className="text-3xl font-bold text-gray-800 mb-4">Earnings Overview</h2>
                  <p className="text-xl text-gray-600">Your potential monthly earnings with BNM</p>
                </div>

                {/* Earning Potential Cards */}
                <div className="grid md:grid-cols-3 gap-8">
                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center">
                    <h3 className="text-2xl font-bold text-purple-600 mb-2">₹25,000</h3>
                    <p className="text-gray-600 mb-4 font-semibold">Part-time (15-20 hrs/week)</p>
                    <ul className="text-sm text-gray-500 space-y-2 text-left">
                      <li>• 3-4 services per day</li>
                      <li>• Flexible timing</li>
                      <li>• Weekend availability</li>
                      <li>• Perfect for students</li>
                    </ul>
                  </div>

                  <div className="bg-purple-600 text-white rounded-xl p-8 shadow-lg transform scale-105">
                    <h3 className="text-2xl font-bold mb-2">₹40,000</h3>
                    <p className="text-purple-100 mb-4 font-semibold">Full-time (35-40 hrs/week)</p>
                    <ul className="text-sm text-purple-100 space-y-2 text-left">
                      <li>• 6-8 services per day</li>
                      <li>• Peak hour bonuses</li>
                      <li>• Priority job allocation</li>
                      <li>• Professional growth</li>
                    </ul>
                  </div>

                    <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center">
                    <h3 className="text-2xl font-bold text-green-600 mb-2">₹60,000+</h3>
                    <p className="text-gray-600 mb-4 font-semibold">Expert Level (45+ hrs/week)</p>
                    <ul className="text-sm text-gray-500 space-y-2 text-left">
                      <li>• 10+ services per day</li>
                      <li>• Premium service rates</li>
                      <li>• Multiple skill bonuses</li>
                      <li>• Team leadership opportunities</li>
                    </ul>
                  </div>
                </div>

                {/* Key Benefits */}
                <div className="bg-purple-50 rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Why Partners Choose BNM</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-money-rupee-circle-line text-white text-2xl"></i>
                      </div>
                      <h4 className="font-semibold text-gray-800 mb-1">Weekly Payouts</h4>
                      <p className="text-sm text-gray-600">Get paid every Monday</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-gift-line text-white text-2xl"></i>
                      </div>
                      <h4 className="font-semibold text-gray-800 mb-1">Zero Commission</h4>
                      <p className="text-sm text-gray-600">First 10 jobs commission-free</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-time-line text-white text-2xl"></i>
                      </div>
                      <h4 className="font-semibold text-gray-800 mb-1">Flexible Hours</h4>
                      <p className="text-sm text-gray-600">Work when you want</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-shield-check-line text-white text-2xl"></i>
                      </div>
                      <h4 className="font-semibold text-gray-800 mb-1">Insurance Coverage</h4>
                      <p className="text-sm text-gray-600">Full protection included</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Calculation Tab */}
            {selectedTab === 'calculation' && (
              <div className="space-y-12">
                <div className="text-center">
                  <h2 className="text-3xl font-bold text-gray-800 mb-4">How Earnings Are Calculated</h2>
                  <p className="text-xl text-gray-600">Transparent breakdown of your earnings per service</p>
                </div>

                {/* Earning Formula */}
                <div className="bg-white rounded-xl p-8 shadow-sm border-2 border-purple-200">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">Your Earnings Formula</h3>
                  <div className="text-center text-lg">
                    <span className="bg-purple-100 px-4 py-2 rounded-lg font-semibold text-purple-800">
                      Service Fee × 80% + Performance Bonus + Peak Hour Bonus
                    </span>
                  </div>
                  <div className="mt-6 grid md:grid-cols-3 gap-4 text-center">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-800">Base Commission</h4>
                      <p className="text-purple-600 text-xl font-bold">80%</p>
                      <p className="text-sm text-gray-600">of service fee</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-800">Performance Bonus</h4>
                      <p className="text-green-600 text-xl font-bold">Up to 15%</p>
                      <p className="text-sm text-gray-600">extra for high ratings</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-800">Peak Hour Bonus</h4>
                      <p className="text-orange-600 text-xl font-bold">₹50</p>
                      <p className="text-sm text-gray-600">per peak hour job</p>
                    </div>
                  </div>
                </div>

                {/* Service Examples */}
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Earnings Examples</h3>
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-purple-50">
                          <tr>
                            <th className="px-6 py-4 text-left font-semibold text-gray-800">Service</th>
                            <th className="px-6 py-4 text-center font-semibold text-gray-800">Base Price</th>
                            <th className="px-6 py-4 text-center font-semibold text-gray-800">Your Share (80%)</th>
                            <th className="px-6 py-4 text-center font-semibold text-gray-800">Bonus</th>
                            <th className="px-6 py-4 text-center font-semibold text-gray-800">Total Earning</th>
                            <th className="px-6 py-4 text-center font-semibold text-gray-800">Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {earningExamples.map((example, index) => (
                            <tr key={index} className="border-t border-gray-100">
                              <td className="px-6 py-4 font-medium text-gray-800">{example.service}</td>
                              <td className="px-6 py-4 text-center text-gray-600">{example.basePrice}</td>
                              <td className="px-6 py-4 text-center text-purple-600 font-semibold">{example.commission}</td>
                              <td className="px-6 py-4 text-center text-green-600">{example.bonus}</td>
                              <td className="px-6 py-4 text-center text-gray-800 font-bold">{example.total}</td>
                              <td className="px-6 py-4 text-center text-gray-500">{example.time}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Maximize Tab */}
            {selectedTab === 'maximize' && (
              <div className="space-y-12">
                <div className="text-center">
                  <h2 className="text-3xl font-bold text-gray-800 mb-4">Maximize Your Earnings</h2>
                  <p className="text-xl text-gray-600">Pro tips to boost your income as a BNM partner</p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {tips.map((tip, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                        <i className={`${tip.icon} text-xl text-purple-600`}></i>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">{tip.title}</h3>
                      <p className="text-gray-600">{tip.desc}</p>
                    </div>
                  ))}
                </div>

                {/* Success Story */}
                <div className="bg-gradient-to-r from-purple-100 to-purple-50 rounded-xl p-8">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">Success Story</h3>
                    <p className="text-gray-600">How Rajesh increased his earnings by 150%</p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div>
                      <div className="flex items-center mb-4">
                        <img
                          src="https://readdy.ai/api/search-image?query=professional%20Indian%20service%20worker%20smiling%20with%20tools%2C%20success%20story%20portrait%2C%20confident%20handyman%20with%20purple%20shirt%2C%20clean%20professional%20headshot&width=100&height=100&seq=rajesh-success&orientation=squarish"
                          alt="Rajesh"
                          className="w-16 h-16 rounded-full object-cover mr-4"
                        />
                        <div>
                          <h4 className="font-semibold text-gray-800">Rajesh Kumar</h4>
                          <p className="text-gray-600">Electrician, Nagpur</p>
                        </div>
                      </div>
                      <p className="text-gray-700 mb-4">
                        "I started with just electrical repairs, earning ₹20,000/month. After learning plumbing and AC repair, plus maintaining 4.9 stars, I now earn ₹50,000+ monthly!"
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center bg-white p-4 rounded-lg">
                        <h4 className="text-2xl font-bold text-red-500">₹20K</h4>
                        <p className="text-sm text-gray-600">Before (1 skill)</p>
                      </div>
                      <div className="text-center bg-white p-4 rounded-lg">
                        <h4 className="text-2xl font-bold text-green-500">₹50K+</h4>
                        <p className="text-sm text-gray-600">After (3 skills)</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payouts Tab */}
            {selectedTab === 'payouts' && (
              <div className="space-y-12">
                <div className="text-center">
                  <h2 className="text-3xl font-bold text-gray-800 mb-4">Payment & Payouts</h2>
                  <p className="text-xl text-gray-600">How and when you receive your earnings</p>
                </div>

                {/* Payout Schedule */}
                <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                  <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">Weekly Payout Schedule</h3>
                  <div className="grid md:grid-cols-7 gap-4">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                      <div
                        key={day}
                        className={`p-4 rounded-lg text-center ${
                          day === 'Mon' ? 'bg-green-100 border-2 border-green-500' : 'bg-gray-50'
                        }`}
                      >
                        <h4 className="font-semibold text-gray-800">{day}</h4>
                        {day === 'Mon' && (
                          <div className="mt-2">
                            <i className="ri-bank-card-line text-green-600 text-xl"></i>
                            <p className="text-xs text-green-600 font-medium mt-1">Payout Day</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 text-center">
                    <p className="text-gray-600">Earnings from Monday to Sunday are paid out the following Monday</p>
                  </div>
                </div>

                {/* Payment Process */}
                <div className="grid md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-blue-600">1</span>
                    </div>
                    <h3 className="font-semibold text-gray-800 mb-2">Complete Service</h3>
                    <p className="text-sm text-gray-600">Finish job and get customer confirmation</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-blue-600">2</span>
                    </div>
                    <h3 className="font-semibold text-gray-800 mb-2">Automatic Calculation</h3>
                    <p className="text-sm text-gray-600">System calculates your earnings including bonuses</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-blue-600">3</span>
                    </div>
                    <h3 className="font-semibold text-gray-800 mb-2">Weekly Processing</h3>
                    <p className="text-sm text-gray-600">All earnings processed every Monday</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-green-600">4</span>
                    </div>
                    <h3 className="font-semibold text-gray-800 mb-2">Direct Deposit</h3>
                    <p className="text-sm text-gray-600">Money transferred to your bank account</p>
                  </div>
                </div>

                {/* Payment FAQs */}
                <div className="bg-purple-50 rounded-xl p-8">
                  <h3 className="text-xl font-bold text-gray-800 mb-6">Payment FAQs</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">What if Monday is a holiday?</h4>
                      <p className="text-gray-600">Payments are processed on the next working day.</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">How do I track my earnings?</h4>
                      <p className="text-gray-600">Use the Partner App to view real-time earnings and payout history.</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">Are there any deductions?</h4>
                      <p className="text-gray-600">Only standard TDS as per government regulations.</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">What if I don&apos;t receive payment?</h4>
                      <p className="text-gray-600">
                        Contact partner support immediately. 99.9% payments are processed on time.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Bonuses Tab */}
            {selectedTab === 'bonuses' && (
              <div className="space-y-12">
                <div className="text-center">
                  <h2 className="text-3xl font-bold text-gray-800 mb-4">Bonuses & Incentives</h2>
                  <p className="text-xl text-gray-600">Extra ways to boost your earnings with BNM</p>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  {bonusStructure.map((bonus, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-start">
                        <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                          <i className={`${bonus.icon} text-xl text-purple-600`}></i>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-800 mb-2">{bonus.type}</h3>
                          <p className="text-gray-600 mb-3">{bonus.criteria}</p>
                          <div className="bg-green-50 px-3 py-2 rounded-lg inline-block">
                            <span className="text-green-700 font-semibold">{bonus.reward}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bonus Calculator */}
                <div className="bg-gradient-to-r from-purple-100 to-purple-50 rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Monthly Bonus Potential</h3>
                  <div className="grid md:grid-cols-3 gap-6 text-center">
                    <div className="bg-white p-6 rounded-lg">
                      <h4 className="text-lg font-semibold text-gray-800 mb-2">Good Performance</h4>
                      <p className="text-3xl font-bold text-purple-600 mb-2">₹5,000</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• 4.0+ rating</li>
                        <li>• 15 jobs/week</li>
                        <li>• Some peak hours</li>
                      </ul>
                    </div>
                    <div className="bg-white p-6 rounded-lg border-2 border-purple-500">
                      <h4 className="text-lg font-semibold text-gray-800 mb-2">Excellent Performance</h4>
                      <p className="text-3xl font-bold text-purple-600 mb-2">₹12,000</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• 4.5+ rating</li>
                        <li>• 25 jobs/week</li>
                        <li>• Regular peak hours</li>
                      </ul>
                    </div>
                    <div className="bg-white p-6 rounded-lg">
                      <h4 className="text-lg font-semibold text-gray-800 mb-2">Top Performer</h4>
                      <p className="text-3xl font-bold text-green-600 mb-2">₹20,000+</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• 4.8+ rating</li>
                        <li>• 35+ jobs/week</li>
                        <li>• All bonuses active</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-purple-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Start Earning?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join thousands of partners who are already earning steady income with BNM
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/apply"
              className="bg-white text-purple-600 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Apply to Become Partner
            </Link>
            <Link
              to="/partner-support"
              className="bg-purple-700 text-white px-8 py-4 rounded-full font-semibold hover:bg-purple-800 transition-colors cursor-pointer whitespace-nowrap border-2 border-purple-700"
            >
              Contact Partner Support
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img
                src="https://static.readdy.ai/image/10053dadbce1851b7032d4a2f38526c3/83173065781489fd4e90ffc998d6a3d9.jfif"
                alt="Baainearme"
                className="h-8 mb-4"
              />
              <p className="text-gray-400">
                Helping partners maximize their earning potential
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">For Partners</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link to="/apply" className="hover:text-white cursor-pointer">
                    Apply Now
                  </Link>
                </li>
                <li>
                  <Link to="/partner-support" className="hover:text-white cursor-pointer">
                    Partner Support
                  </Link>
                </li>
                <li>
                  <Link to="/earnings-guide" className="hover:text-white cursor-pointer">
                    Earnings Guide
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link to="/customer" className="hover:text-white cursor-pointer">
                    For Customers
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="hover:text-white cursor-pointer">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/faqs" className="hover:text-white cursor-pointer">
                    FAQs
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link to="/partner-terms" className="hover:text-white cursor-pointer">
                    Terms & Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/partner-privacy" className="hover:text-white cursor-pointer">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/refund-policy" className="hover:text-white cursor-pointer">
                    Refund Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Baainearme. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
